import metadata from '../metadata.json';

// Faucet Consumption
const faucetConsumption = (equipment, usage) => {
  const roomMultiplier = equipment.location?.room_multiplier ?? 1;
  if (usage.is_known_volume) {
    return usage.annual_volume * 1000 * roomMultiplier;
  } else {
    const waterValue = equipment.water_value ?? equipment.technical_data?.max_flow ?? 0;
    const dailyVolume = usage.usage_data.daily_usage_type === "per_person"
      ? usage.usage_data.daily_usage * waterValue * usage.usage_data.nb_users
      : usage.usage_data.daily_usage * waterValue;
    const nbNormalWeeks = usage.usage_data.yearly_usage - usage.usage_data.less_intense_weeks - usage.usage_data.more_intense_weeks;
    const nbReelWeeks =
      nbNormalWeeks * 1 +
      usage.usage_data.more_intense_weeks * metadata.more_intense_week_effect +
      usage.usage_data.less_intense_weeks * metadata.less_intense_week_effect;
    return dailyVolume * usage.usage_data.weekly_usage * nbReelWeeks * roomMultiplier;
  }
};

// WC Consumption
const wcConsumption = (equipment, usage) => {
  const roomMultiplier = equipment.location?.room_multiplier ?? 1;
  if (usage.is_known_volume) {
    return usage.annual_volume * 1000 * roomMultiplier;
  } else {
    const td = equipment.technical_data;
    const volumeOneUse = td.flush_type === "dual_flush"
      ? td.flush_volume_max * metadata.data_wc.factor_max_flush +
        td.flush_volume_min * metadata.data_wc.factor_min_flush
      // water_value encodes simple_flush (flush_volume_max) or interruptible_flush (flush_flow)
      : (equipment.water_value ?? td.flush_volume_max ?? 0);
    const dailyVolume = usage.usage_data.daily_usage_type === "per_person"
      ? usage.usage_data.daily_usage * volumeOneUse * usage.usage_data.nb_users
      : usage.usage_data.daily_usage * volumeOneUse;
    const nbNormalWeeks = usage.usage_data.yearly_usage - usage.usage_data.less_intense_weeks - usage.usage_data.more_intense_weeks;
    const nbReelWeeks =
      nbNormalWeeks * 1 +
      usage.usage_data.more_intense_weeks * metadata.more_intense_week_effect +
      usage.usage_data.less_intense_weeks * metadata.less_intense_week_effect;
    return dailyVolume * usage.usage_data.weekly_usage * nbReelWeeks * roomMultiplier;
  }
};

// Urinal Consumption
const urinalConsumption = (equipment, usage) => {
  const roomMultiplier = equipment.location?.room_multiplier ?? 1;
  if (usage.is_known_volume) {
    return usage.annual_volume * 1000 * roomMultiplier;
  } else {
    const volumeOneUse = equipment.water_value ?? equipment.technical_data?.flush_volume ?? 0
    const dailyVolume = usage.usage_data.daily_usage_type === "per_person"
      ? usage.usage_data.daily_usage * volumeOneUse * usage.usage_data.nb_users
      : usage.usage_data.daily_usage * volumeOneUse;
    const nbNormalWeeks = usage.usage_data.yearly_usage - usage.usage_data.less_intense_weeks - usage.usage_data.more_intense_weeks;
    const nbReelWeeks =
      nbNormalWeeks * 1 +
      usage.usage_data.more_intense_weeks * metadata.more_intense_week_effect +
      usage.usage_data.less_intense_weeks * metadata.less_intense_week_effect;
    return dailyVolume * usage.usage_data.weekly_usage * nbReelWeeks * roomMultiplier;
  }
};

// Machine Consumption
const machineConsumption = (equipment, usage) => {
  const roomMultiplier = equipment.location?.room_multiplier ?? 1;
  if (usage.is_known_volume) {
    return usage.annual_volume * 1000 * roomMultiplier;
  } else {
    const waterValue = equipment.water_value ?? equipment.technical_data?.volume_per_cycle ?? 0;
    const dailyVolume = usage.usage_data.daily_usage_type === "per_person"
          ? usage.usage_data.daily_usage * waterValue * usage.usage_data.nb_users
          : usage.usage_data.daily_usage * waterValue;
    const nbNormalWeeks = usage.usage_data.yearly_usage - usage.usage_data.less_intense_weeks - usage.usage_data.more_intense_weeks;
    const nbReelWeeks =
      nbNormalWeeks * 1 +
      usage.usage_data.more_intense_weeks * metadata.more_intense_week_effect +
      usage.usage_data.less_intense_weeks * metadata.less_intense_week_effect;
    return dailyVolume * usage.usage_data.weekly_usage * nbReelWeeks * roomMultiplier;
  }
};

// Shower Consumption
const showerConsumption = (equipment, usage) => {
  const roomMultiplier = equipment.location?.room_multiplier ?? 1;
  if (usage.is_known_volume) {
    return usage.annual_volume * 1000 * roomMultiplier;
  } else {
    const waterValue = equipment.water_value ?? equipment.technical_data?.max_flow ?? 0;
    const dailyVolume = usage.usage_data.daily_usage_type === "per_person"
          ? usage.usage_data.daily_usage * waterValue * usage.usage_data.nb_users
          : usage.usage_data.daily_usage * waterValue;
    const nbNormalWeeks = usage.usage_data.yearly_usage - usage.usage_data.less_intense_weeks - usage.usage_data.more_intense_weeks;
    const nbReelWeeks =
      nbNormalWeeks * 1 +
      usage.usage_data.more_intense_weeks * metadata.more_intense_week_effect +
      usage.usage_data.less_intense_weeks * metadata.less_intense_week_effect;
    return dailyVolume * usage.usage_data.weekly_usage * nbReelWeeks * roomMultiplier;
  }
};

// Consommation réelle de référence (m³/an) comparée à l'estimation : la valeur saisie
// (global_water_data.reference_consumption) si > 0, sinon le relevé de l'année N.
// Miroir : water_volume_computation.py:get_reference_consumption.
export const getReferenceConsumption = (globalWaterData) => {
  const reference = globalWaterData?.reference_consumption;
  if (typeof reference === 'number' && reference > 0) return reference;
  return globalWaterData?.water_consumption_year_3 ?? null;
};

// Per-usage annual volume in m³ (requires equipment object with water_value / technical_data)
export const computeAnnualVolumeM3 = (equipment, usage) => {
  if (!equipment || !usage) return 0;
  let liters = 0;
  try {
    switch (equipment.type_equipment) {
      case 'faucet':  liters = faucetConsumption(equipment, usage);  break;
      case 'wc':      liters = wcConsumption(equipment, usage);       break;
      case 'urinal':  liters = urinalConsumption(equipment, usage);   break;
      case 'machine': liters = machineConsumption(equipment, usage);  break;
      case 'shower':  liters = showerConsumption(equipment, usage);   break;
      default:        return 0;
    }
  } catch {
    return 0;
  }
  if (liters === null || liters === undefined || isNaN(liters)) return 0;
  return liters / 1000;
};

// Main Consumption Calculation Component
export const getClientConsumptionSummary = (client, equipments, usages) => {
  let result = {
    equipments: {},
    client_summary: {
      client_name: client.name_client || 'Unknown',
      total_consumption: 0,
    },
  };
  let totalConsumption = 0;

  equipments.forEach((equipment) => {
    const equipmentName = `${equipment.name_equipment}_${equipment._id}`;

    if (!result.equipments[equipmentName]) {
      result.equipments[equipmentName] = {};
    }

    usages.forEach((usage) => {
      if (equipment._id === usage._id_equipement) {

        const usageName = usage.type_usage;
        const functionalUnit = usage.functional_unit || 'unknown';
        let consumption = 0;

        switch (equipment.type_equipment) {
          case 'faucet':
            consumption = faucetConsumption(equipment, usage);
            break;
          case 'wc':
            consumption = wcConsumption(equipment, usage);
            break;
          case 'urinal':
            consumption = urinalConsumption(equipment, usage);
            break;
          case 'machine':
            consumption = machineConsumption(equipment, usage);
            break;
          case 'shower':
            consumption = showerConsumption(equipment, usage);
            break;
          default:
            consumption = 0;
        }

        if (consumption !== null && !isNaN(consumption)) {
          const annualVolume = consumption / 1000;

          // 🟢 Si un usage du même nom existe déjà → on additionne
          if (result.equipments[equipmentName][usageName]) {
            result.equipments[equipmentName][usageName].annual_volume += annualVolume;
          } else {
            // Sinon on créé l’entrée
            result.equipments[equipmentName][usageName] = {
              functional_unit: functionalUnit,
              annual_volume: annualVolume,
            };
          }

          totalConsumption += annualVolume;
        }
      }
    });
  });


  result.client_summary.total_consumption = totalConsumption;
  return result;
};
